import { useApolloClient } from "@apollo/client";
import { useChatSessionStatusChangedSubscription } from "@qw/graphql";

/**
 * Single app-internal subscription that mirrors server-pushed chat
 * session status changes into Apollo's normalized cache. Every consumer
 * reading a `ChatSession` entity through any query (`ChatSession`,
 * `ChatSessions`, fragment-derived reads) automatically sees the
 * updated `status` field via Apollo's cache broadcasts — no
 * per-consumer refetch wiring required.
 *
 * Mount one of these per chat experience (inside `ChatProvider`, see
 * `chat-provider.tsx`). Two mounts in the same React tree are harmless
 * (Apollo dedupes overlapping subscription documents) but wasteful.
 *
 * The subscription is user-scoped — the server filter requires
 * `event.userId === variables.userId`, so pass the chat session's
 * owner's id (which is the current user for normal access, or
 * verified separately for internal users via the server filter).
 *
 * What this DOES NOT cover: queries whose list membership is filtered
 * by status (e.g., `ChatSessions` with `input.status: "AWAITING_USER_INPUT"`).
 * A cache field update doesn't re-evaluate the server-side WHERE
 * clause, so those queries still need their own refetch trigger.
 * `awaiting-input-dropdown.tsx`'s separate subscription handles that
 * specific case.
 */
export const useChatSessionStatusLiveCache = ({ userId }: { userId: string | undefined | null }) => {
  const client = useApolloClient();
  useChatSessionStatusChangedSubscription({
    variables: { userId: userId ?? undefined },
    skip: !userId,
    onData: ({ data }) => {
      const event = data.data?.chatSessionStatusChanged;
      if (!event) {
        return;
      }
      // `cache.modify` is targeted at the normalized `ChatSession:{id}`
      // entity. Every cached query that included this entity (single-
      // session, list, fragment-derived) updates its view of the
      // `status` field; nothing else is touched.
      client.cache.modify({
        id: client.cache.identify({ __typename: "ChatSession", id: event.chatSessionId }),
        fields: {
          status: () => event.status,
        },
      });
    },
  });
};
